import { useState } from 'react';
import { restaurantTasksAPI, uploadFile, deleteAttachment } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentGallery } from './AttachmentGallery';
import { ChecklistEditor, checklistOps } from './ChecklistEditor';
import { CommentsSection } from './CommentsSection';
import { PendingFilesList, validateFiles } from './FileFields';
import { RT_STATUSES, RT_TYPES, RT_TYPE_MAP } from '../lib/restaurantConstants';
import { getDaysInProgress } from '../lib/format';

export default function RestaurantTaskModal({
  task,
  restaurants = [],
  users = [],
  lockedRestaurantId = null,
  canEdit = true,
  currentUser,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    task_type: task?.task_type || 'negotiations',
    status: task?.status || 'todo',
    restaurant_id: task?.restaurant_id || lockedRestaurantId || '',
    assignee_id: task?.assignee_id || '',
    due_date: task?.due_date || null,
    checklist: task?.checklist || [],
    link: task?.link || '',
  });

  const isEdit = !!task?.id;
  const age = getDaysInProgress(task);
  const typeMeta = RT_TYPE_MAP[formData.task_type] || RT_TYPES[0];

  const setChecklist = (fn) => setFormData(f => ({ ...f, checklist: fn(f.checklist) }));

  const handleSave = async () => {
    if (!formData.title.trim()) { toast.error('Title is required'); return; }
    if (!formData.restaurant_id) { toast.error('Restaurant is required'); return; }
    setLoading(true);
    try {
      let taskId = task?.id;
      if (isEdit) {
        await restaurantTasksAPI.update(taskId, formData);
      } else {
        const res = await restaurantTasksAPI.create(formData);
        taskId = res.data.id;
      }
      for (const f of pendingFiles) {
        try {
          await uploadFile(f, null, null, null, null, taskId);
        } catch (err) {
          toast.error(`Failed to upload ${f.name}`);
        }
      }
      toast.success(isEdit ? 'Task updated' : 'Task created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this task?')) return;
    setLoading(true);
    try {
      await restaurantTasksAPI.delete(task.id);
      toast.success('Task deleted');
      onSaved();
    } catch (err) {
      toast.error('Failed to delete task');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteAttachment(attachmentId);
      toast.success('File deleted');
      onSaved();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-[620px] max-h-[95vh] sm:max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="restaurant-task-modal"
      >
        <div className="h-1 w-full" style={{ backgroundColor: typeMeta.color }} />
        <div className="flex items-center justify-between p-4 border-b border-[#E0E0E0]">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-lg font-semibold text-[#111111] truncate">{isEdit ? 'Task Details' : 'New Restaurant Task'}</h2>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
              style={{ backgroundColor: `${typeMeta.color}15`, color: typeMeta.color }}
              data-testid={`rt-type-badge-${formData.task_type}`}
            >
              {typeMeta.label}
            </span>
            {age && (
              <span className="inline-flex items-center gap-1 text-xs text-[#666666] flex-shrink-0" data-testid="rt-modal-age">
                <Clock size={12} /> {age.days}d{age.frozen ? ' (done)' : ''}
              </span>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="rt-close-btn"><X size={20} /></Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} placeholder="Task title" disabled={!canEdit} data-testid="rt-title-input" />
          </div>
          <div>
            <Label>Description <span className="text-[#999999] font-normal">(optional)</span></Label>
            <Textarea value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} rows={3} disabled={!canEdit} data-testid="rt-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={formData.task_type} onValueChange={v => setFormData(f => ({ ...f, task_type: v }))} disabled={!canEdit}>
                <SelectTrigger data-testid="rt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RT_TYPES.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`rt-type-option-${opt.value}`}>
                      <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />{opt.label}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={v => setFormData(f => ({ ...f, status: v }))} disabled={!canEdit}>
                <SelectTrigger data-testid="rt-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RT_STATUSES.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Restaurant</Label>
              {lockedRestaurantId ? (
                <p className="text-sm text-[#666666] mt-2">{restaurants.find(r => r.id === lockedRestaurantId)?.name || task?.restaurant_name || '—'}</p>
              ) : (
                <Select value={formData.restaurant_id || undefined} onValueChange={v => setFormData(f => ({ ...f, restaurant_id: v }))} disabled={!canEdit}>
                  <SelectTrigger data-testid="rt-restaurant"><SelectValue placeholder="Select restaurant" /></SelectTrigger>
                  <SelectContent>
                    {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={formData.assignee_id || 'none'} onValueChange={v => setFormData(f => ({ ...f, assignee_id: v === 'none' ? '' : v }))} disabled={!canEdit}>
                <SelectTrigger data-testid="rt-assignee"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due date <span className="text-[#999999] font-normal">(optional)</span></Label>
              <Input
                type="date"
                value={formData.due_date || ''}
                onChange={e => setFormData(f => ({ ...f, due_date: e.target.value || null }))}
                disabled={!canEdit}
                data-testid="rt-due-date"
              />
            </div>
            <div>
              <Label>Link <span className="text-[#999999] font-normal">(optional)</span></Label>
              <Input value={formData.link} onChange={e => setFormData(f => ({ ...f, link: e.target.value }))} placeholder="https://..." disabled={!canEdit} data-testid="rt-link" />
            </div>
          </div>

          <ChecklistEditor
            checklist={formData.checklist}
            canEdit={canEdit}
            onToggle={(id) => setChecklist(l => checklistOps.toggle(l, id))}
            onAdd={(text) => setChecklist(l => checklistOps.add(l, text))}
            onRemove={(id) => setChecklist(l => checklistOps.remove(l, id))}
            onEdit={(id, text) => setChecklist(l => checklistOps.edit(l, id, text))}
            onReorder={(fromId, toId) => setChecklist(l => checklistOps.reorder(l, fromId, toId))}
          />

          <div>
            <Label>Attachments <span className="text-[#999999] font-normal">(optional)</span></Label>
            <AttachmentGallery attachments={task?.attachments || []} canDelete={canEdit} onDelete={handleDeleteAttachment} />
            <PendingFilesList pendingFiles={pendingFiles} onRemovePending={(idx) => setPendingFiles(p => p.filter((_, i) => i !== idx))} />
            {canEdit && (
              <Input
                type="file"
                multiple
                onChange={e => { validateFiles(e.target.files, (files) => setPendingFiles(p => [...p, ...files])); e.target.value = ''; }}
                className="mt-2"
                data-testid="rt-file-input"
              />
            )}
          </div>

          {isEdit && (
            <CommentsSection
              entityId={task.id}
              getComments={restaurantTasksAPI.getComments}
              addComment={restaurantTasksAPI.addComment}
              currentUser={currentUser}
            />
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-[#E0E0E0]">
          <div>
            {canEdit && isEdit && (
              <Button variant="destructive" onClick={handleDelete} disabled={loading} data-testid="rt-delete-btn">Delete</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="rt-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={loading || !canEdit} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="rt-save-btn">
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
